// The assistant's React context and the hooks pages use. Kept apart from the provider so modules can use
// them without importing the provider, which imports every module

import { createContext, useContext, useEffect, useRef, type ReactNode } from "react"
import type { ModuleRegistry } from "@/lib/assistant/registry"
import type * as store from "@/lib/assistant/store"
import type { ApprovalRequest, AssistantModule, DisplayMessage, QuestionAnswer, QuestionRequest } from "@/lib/assistant/types"
import type { useCorpora } from "@/pages/dev/smart-cms/shared/corpus/useCorpora"

export interface PendingApproval {
  id: string
  // A plain approve/reject card, a module's own review UI, or a question for the user
  kind: "confirm" | "custom" | "question"
  req?: ApprovalRequest
  question?: QuestionRequest
  render?: (done: (value: any) => void) => ReactNode
  resolve: (value: any) => void
  fallback: any
}

export interface AssistantContextValue {
  registry: ModuleRegistry
  enabled: boolean
  open: boolean
  setOpen: (open: boolean) => void
  openWith: (draft?: string) => void
  draft: string
  setDraft: (text: string) => void

  messages: DisplayMessage[]
  busy: boolean
  // Screenshots are data URLs from lib/assistant/images
  send: (text: string, images?: string[]) => Promise<void>
  stop: () => void
  newChat: () => void

  conversationId: string | null
  conversations: store.ConversationSummary[]
  loadConversation: (id: string) => Promise<void>
  deleteConversation: (id: string) => Promise<void>

  memory: store.MemoryNote[]
  forgetMemory: (id: string) => Promise<void>
  skills: store.AssistantSkill[]
  saveSkill: (skill: Omit<store.AssistantSkill, "id"> & { id?: string }) => Promise<string | null>
  deleteSkill: (id: string) => Promise<void>
  persistence: "ok" | "missing"

  model: string
  setModel: (model: string) => void
  autoAccept: boolean
  setAutoAccept: (on: boolean) => void
  canAutoAccept: boolean
  canConfigure: boolean

  approvals: PendingApproval[]
  resolveApproval: (id: string, value: any) => void
  requestApproval: (req: ApprovalRequest) => Promise<boolean>
  requestCustom: <T>(render: (done: (value: T) => void) => ReactNode, fallback: T) => Promise<T>
  // Null when the user skips it or the reply is stopped
  askQuestion: (q: QuestionRequest) => Promise<QuestionAnswer | null>
  // Loads this address with a full page reload once the current reply ends, reopening the chat after
  reloadWhenDone: (url: string) => void

  labelFor: (toolName: string) => string
  openModules: AssistantModule[]
  pageTitle?: string
  corpora: ReturnType<typeof useCorpora>
}

export const AssistantContext = createContext<AssistantContextValue | null>(null)

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext)
  if (!ctx) throw new Error("useAssistant must be used inside AssistantProvider")
  return ctx
}

// Registers a module while the calling component is mounted. `build` runs before every step
export function useAssistantModule(id: string, build: () => AssistantModule | null, active = true) {
  const ctx = useContext(AssistantContext)
  const buildRef = useRef(build)
  buildRef.current = build
  useEffect(() => {
    if (!ctx || !active) return
    return ctx.registry.register(id, () => buildRef.current())
  }, [ctx?.registry, id, active]) // eslint-disable-line react-hooks/exhaustive-deps
}
